import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { Plus, X, Eye, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORY_COLORS = ['#D4AF37', '#4CAF50', '#2196F3', '#E91E63', '#FF9800', '#9C27B0', '#00BCD4', '#F44336', '#3F51B5', '#607D8B'];

const StoriesBar = ({ onClose }) => {
  const { user, token } = useAuth();
  const { isDarkMode } = useTheme();
  const [storyGroups, setStoryGroups] = useState([]);
  const [viewingStory, setViewingStory] = useState(null);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(onClose ? true : false);
  const [storyText, setStoryText] = useState('');
  const [selectedColor, setSelectedColor] = useState(STORY_COLORS[0]);
  const [creating, setCreating] = useState(false);
  const progressRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchStories();
    const interval = setInterval(fetchStories, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStories = async () => {
    try {
      const res = await axios.get(`${API}/stories`, { headers: { Authorization: `Bearer ${token}` } });
      setStoryGroups(res.data);
    } catch {}
  };

  const createTextStory = async () => {
    if (!storyText.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/stories`, {
        content: storyText.trim(),
        background_color: selectedColor,
        story_type: 'text'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Story posted!');
      setShowCreateModal(false);
      setStoryText('');
      fetchStories();
    } catch { toast.error('Failed to post story'); }
    setCreating(false);
  };

  const createImageStory = async (file) => {
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      await axios.post(`${API}/stories`, {
        media_url: uploadRes.data.url.startsWith('http') ? uploadRes.data.url : `${BACKEND_URL}${uploadRes.data.url}`,
        story_type: 'image'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Story posted!');
      setShowCreateModal(false);
      fetchStories();
    } catch { toast.error('Failed to post story'); }
    setCreating(false);
  };

  const openStoryViewer = (groupIndex) => {
    setViewingStory(groupIndex);
    setCurrentStoryIndex(0);
    markViewed(storyGroups[groupIndex].stories[0].id);
    startTimer();
  };

  const markViewed = async (storyId) => {
    try {
      await axios.post(`${API}/stories/${storyId}/view`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
  };

  const startTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => nextStory(), 5000);
  };

  const nextStory = () => {
    if (viewingStory === null) return;
    const group = storyGroups[viewingStory];
    if (currentStoryIndex < group.stories.length - 1) {
      const nextIdx = currentStoryIndex + 1;
      setCurrentStoryIndex(nextIdx);
      markViewed(group.stories[nextIdx].id);
      startTimer();
    } else if (viewingStory < storyGroups.length - 1) {
      const nextGroup = viewingStory + 1;
      setViewingStory(nextGroup);
      setCurrentStoryIndex(0);
      markViewed(storyGroups[nextGroup].stories[0].id);
      startTimer();
    } else {
      closeViewer();
    }
  };

  const prevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
      startTimer();
    }
  };

  const closeViewer = () => {
    setViewingStory(null);
    setCurrentStoryIndex(0);
    if (timerRef.current) clearTimeout(timerRef.current);
    fetchStories();
  };

  const myStories = storyGroups.find(g => g.user.id === user?.id);
  const otherStories = storyGroups.filter(g => g.user.id !== user?.id);

  return (
    <>
      {/* Stories Bar */}
      <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
        <div className="flex gap-3 overflow-x-auto scrollbar-hide">
          {/* My Story / Add Story */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex flex-col items-center gap-1 flex-shrink-0"
            data-testid="add-story-btn"
          >
            <div className="relative">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                myStories 
                  ? 'bg-gradient-to-br from-[#D4AF37] to-[#C5A059]' 
                  : isDarkMode ? 'bg-gray-800 border-2 border-dashed border-gray-600' : 'bg-gray-100 border-2 border-dashed border-gray-300'
              }`}>
                {myStories ? (
                  <span className="text-white font-medium text-lg">{user?.display_name?.charAt(0).toUpperCase()}</span>
                ) : (
                  <Plus size={22} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                )}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#D4AF37] flex items-center justify-center border-2 border-white">
                <Plus size={10} className="text-white" />
              </div>
            </div>
            <span className={`text-xs truncate w-16 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {myStories ? 'My Story' : 'Add'}
            </span>
          </button>

          {/* Other users' stories */}
          {otherStories.map((group, idx) => (
            <button
              key={group.user.id}
              onClick={() => openStoryViewer(storyGroups.indexOf(group))}
              className="flex flex-col items-center gap-1 flex-shrink-0"
              data-testid={`story-${group.user.id}`}
            >
              <div className={`w-14 h-14 rounded-full p-0.5 ${group.has_unseen ? 'bg-gradient-to-br from-[#D4AF37] to-[#C5A059]' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}>
                <div className={`w-full h-full rounded-full flex items-center justify-center text-white font-medium ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                    {group.user.display_name?.charAt(0).toUpperCase()}
                  </div>
                </div>
              </div>
              <span className={`text-xs truncate w-16 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {group.user.display_name?.split(' ')[0]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Create Story Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className={`w-full max-w-sm rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`p-4 flex items-center justify-between border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
              <h3 className={`font-heading text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Create Story</h3>
              <button onClick={() => setShowCreateModal(false)} className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}><X size={20} /></button>
            </div>
            
            {/* Preview */}
            <div className="aspect-[9/16] max-h-[400px] flex items-center justify-center p-8" style={{ backgroundColor: selectedColor }}>
              <textarea
                value={storyText}
                onChange={e => setStoryText(e.target.value)}
                placeholder="Type your status..."
                className="bg-transparent text-white text-center text-xl font-medium w-full resize-none focus:outline-none placeholder:text-white/50"
                rows={4}
                data-testid="story-text-input"
              />
            </div>

            {/* Color Picker */}
            <div className="p-4 flex items-center gap-2 overflow-x-auto">
              {STORY_COLORS.map(color => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  className={`w-8 h-8 rounded-full flex-shrink-0 border-2 transition-transform ${selectedColor === color ? 'border-white scale-110 ring-2 ring-[#D4AF37]' : 'border-transparent'}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>

            {/* Actions */}
            <div className={`p-4 flex gap-3 border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
              <label className={`flex-1 h-12 rounded-full flex items-center justify-center gap-2 cursor-pointer border ${isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && createImageStory(e.target.files[0])} />
                Photo
              </label>
              <button
                onClick={createTextStory}
                disabled={creating || !storyText.trim()}
                className="flex-1 h-12 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="post-story-btn"
              >
                <Send size={16} /> {creating ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Story Viewer */}
      {viewingStory !== null && storyGroups[viewingStory] && (
        <div className="fixed inset-0 bg-black z-[60] flex items-center justify-center" onClick={nextStory}>
          {(() => {
            const group = storyGroups[viewingStory];
            const story = group.stories[currentStoryIndex];
            if (!story) return null;
            
            return (
              <div className="w-full h-full max-w-md mx-auto relative flex flex-col">
                {/* Progress Bars */}
                <div className="absolute top-0 left-0 right-0 flex gap-1 p-2 z-10">
                  {group.stories.map((_, i) => (
                    <div key={i} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-[5000ms] ${
                        i < currentStoryIndex ? 'bg-white w-full' : i === currentStoryIndex ? 'bg-white w-full animate-[grow_5s_linear]' : 'w-0'
                      }`} />
                    </div>
                  ))}
                </div>

                {/* Header */}
                <div className="absolute top-6 left-0 right-0 flex items-center gap-3 px-4 z-10">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                    {group.user.display_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium text-sm">{group.user.display_name}</p>
                    <p className="text-white/60 text-xs">{formatDistanceToNow(new Date(story.created_at), { addSuffix: true })}</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); closeViewer(); }} className="p-2 rounded-full bg-white/10 hover:bg-white/20">
                    <X size={20} className="text-white" />
                  </button>
                </div>

                {/* Story Content */}
                <div className="flex-1 flex items-center justify-center" onClick={e => e.stopPropagation()}>
                  {story.story_type === 'image' && story.media_url ? (
                    <img src={story.media_url} alt="" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center p-12" style={{ backgroundColor: story.background_color || '#D4AF37' }}>
                      <p className="text-white text-2xl font-medium text-center">{story.content}</p>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="absolute inset-0 flex" onClick={e => e.stopPropagation()}>
                  <div className="w-1/3 h-full" onClick={prevStory} />
                  <div className="w-1/3 h-full" />
                  <div className="w-1/3 h-full" onClick={nextStory} />
                </div>

                {/* Viewers count (own stories) */}
                {group.user.id === user?.id && (
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10">
                      <Eye size={16} className="text-white" />
                      <span className="text-white text-sm">{story.viewed_by?.length || 0} views</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
};

export default StoriesBar;
